import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Loader2, Bot, User, ArrowRight, Calculator } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { getLocalizedPath } from '@/config/routes';
import { QuoteBuilder } from '@/components/quote/QuoteBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const translations = {
  nl: {
    placeholder: 'Stel je vraag...',
    title: 'Netblitz Assistent',
    subtitle: 'Vraag me alles over onze diensten',
    welcome: 'Hallo! Ik ben de virtuele assistent van Netblitz. Stel gerust een vraag over onze websites, prijzen of diensten. Of gebruik de **Offerte Calculator** om direct je prijs te berekenen!',
    error: 'Sorry, er ging iets mis. Probeer het opnieuw.',
    sending: 'Even geduld...',
    contactBtn: 'Neem contact op',
    quoteBtn: 'Offerte Calculator',
    backToChat: 'Terug naar chat',
  },
  en: {
    placeholder: 'Ask your question...',
    title: 'Netblitz Assistant',
    subtitle: 'Ask me anything about our services',
    welcome: 'Hello! I\'m Netblitz\'s virtual assistant. Feel free to ask about our websites, pricing, or services. Or use the **Quote Calculator** to calculate your price directly!',
    error: 'Sorry, something went wrong. Please try again.',
    sending: 'Please wait...',
    contactBtn: 'Contact us',
    quoteBtn: 'Quote Calculator',
    backToChat: 'Back to chat',
  },
  fr: {
    placeholder: 'Posez votre question...',
    title: 'Assistant Netblitz',
    subtitle: 'Posez-moi toutes vos questions',
    welcome: 'Bonjour! Je suis l\'assistant virtuel de Netblitz. N\'hésitez pas à poser des questions sur nos sites web, prix ou services. Ou utilisez le **Calculateur de Devis** pour calculer votre prix!',
    error: 'Désolé, une erreur s\'est produite. Veuillez réessayer.',
    sending: 'Patientez...',
    contactBtn: 'Contactez-nous',
    quoteBtn: 'Calculateur de Devis',
    backToChat: 'Retour au chat',
  },
  de: {
    placeholder: 'Stellen Sie Ihre Frage...',
    title: 'Netblitz Assistent',
    subtitle: 'Fragen Sie mich alles über unsere Dienste',
    welcome: 'Hallo! Ich bin der virtuelle Assistent von Netblitz. Fragen Sie gerne nach unseren Websites, Preisen oder Dienstleistungen. Oder nutzen Sie den **Angebots-Rechner** um Ihren Preis zu berechnen!',
    error: 'Entschuldigung, etwas ist schief gelaufen. Bitte versuchen Sie es erneut.',
    sending: 'Bitte warten...',
    contactBtn: 'Kontaktieren Sie uns',
    quoteBtn: 'Angebots-Rechner',
    backToChat: 'Zurück zum Chat',
  },
};

export const ChatbotWidget = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.nl;
  
  const [isOpen, setIsOpen] = useState(false);
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', text: t.welcome, timestamp: new Date(), showQuoteBtn: true }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Update welcome message when language changes
  useEffect(() => {
    if (messages.length === 1 && messages[0].type === 'bot') {
      setMessages([{ type: 'bot', text: t.welcome, timestamp: new Date(), showQuoteBtn: true }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, t.welcome]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Allow other components (e.g. sticky CTA) to open the quote calculator directly
  useEffect(() => {
    const openQuote = () => {
      setIsOpen(true);
      setShowQuoteBuilder(true);
    };
    window.addEventListener('netblitz:open-quote', openQuote);
    return () => window.removeEventListener('netblitz:open-quote', openQuote);
  }, []);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message
    setMessages(prev => [...prev, { 
      type: 'user', 
      text: userMessage, 
      timestamp: new Date() 
    }]);
    
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          language: language,
          session_id: sessionId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      // Add bot response
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: data.answer,
        source: data.source,
        showContactLink: data.show_contact_link,
        quoteCreated: data.quote_created,
        timestamp: new Date() 
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: t.error, 
        isError: true,
        timestamp: new Date() 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Toggle Button with Counter */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            className="fixed bottom-28 lg:bottom-24 right-5 lg:right-6 z-[1000] flex flex-col items-end gap-3"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ delay: 1.2, type: 'spring', stiffness: 200 }}
          >
            {/* Toggle button - BIGGER */}
            <motion.button
              className="w-16 h-16 rounded-full bg-[#2563EB] text-white shadow-xl hover:bg-[#1D4ED8] transition-all flex items-center justify-center"
              onClick={() => setIsOpen(true)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Open chat"
              data-testid="chatbot-toggle-btn"
            >
              <MessageSquare size={32} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="chatbot-header">
              <div className="chatbot-header-info">
                <div className="chatbot-avatar">
                  {showQuoteBuilder ? <Calculator size={24} /> : <Bot size={24} />}
                </div>
                <div>
                  <h3 className="chatbot-title">{showQuoteBuilder ? t.quoteBtn : t.title}</h3>
                  <p className="chatbot-subtitle">{showQuoteBuilder ? '' : t.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {showQuoteBuilder && (
                  <button 
                    className="chatbot-back-btn"
                    onClick={() => setShowQuoteBuilder(false)}
                    aria-label="Back to chat"
                  >
                    {t.backToChat}
                  </button>
                )}
                <button 
                  className="chatbot-close"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close chat"
                  data-testid="chatbot-close-btn"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Quote Builder or Chat */}
            {showQuoteBuilder ? (
              <div className="chatbot-quote-builder">
                <QuoteBuilder 
                  onClose={() => setShowQuoteBuilder(false)}
                  onComplete={(prices) => {
                    setShowQuoteBuilder(false);
                    setMessages(prev => [...prev, {
                      type: 'bot',
                      text: language === 'nl' 
                        ? `Je offerte is aangevraagd! Totaal: €${prices.total}. We nemen binnen 24 uur contact met je op.`
                        : language === 'en'
                        ? `Your quote has been requested! Total: €${prices.total}. We'll contact you within 24 hours.`
                        : language === 'fr'
                        ? `Votre devis a été demandé! Total: €${prices.total}. Nous vous contacterons dans les 24 heures.`
                        : `Ihr Angebot wurde angefordert! Gesamt: €${prices.total}. Wir melden uns innerhalb von 24 Stunden.`,
                      timestamp: new Date()
                    }]);
                  }}
                />
              </div>
            ) : (
              <>
                {/* Messages */}
                <div className="chatbot-messages" data-testid="chatbot-messages">
                  {messages.map((msg, index) => (
                    <motion.div
                      key={index}
                      className={`chatbot-message ${msg.type} ${msg.isError ? 'error' : ''}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="chatbot-message-icon">
                        {msg.type === 'bot' ? <Bot size={16} /> : <User size={16} />}
                      </div>
                      <div className="chatbot-message-content">
                        <p>{msg.text}</p>
                        {msg.showQuoteBtn && (
                          <button 
                            onClick={() => setShowQuoteBuilder(true)}
                            className="chatbot-quote-btn"
                            data-testid="chatbot-quote-btn"
                          >
                            <Calculator size={14} />
                            {t.quoteBtn}
                          </button>
                        )}
                        {msg.showContactLink && (
                          <Link 
                            to={getLocalizedPath('contact', language)} 
                            className="chatbot-contact-btn"
                            onClick={() => setIsOpen(false)}
                            data-testid="chatbot-contact-link"
                          >
                            {t.contactBtn}
                            <ArrowRight size={14} />
                          </Link>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  
                  {isLoading && (
                    <motion.div
                      className="chatbot-message bot loading"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="chatbot-message-icon">
                        <Bot size={16} />
                      </div>
                      <div className="chatbot-message-content">
                        <div className="chatbot-typing">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="chatbot-input-area">
                  <button
                    onClick={() => setShowQuoteBuilder(true)}
                    className="chatbot-calculator-btn"
                    aria-label="Open quote calculator"
                    title={t.quoteBtn}
                    data-testid="chatbot-calculator-btn"
                  >
                    <Calculator size={20} />
                  </button>
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t.placeholder}
                    disabled={isLoading}
                    className="chatbot-input"
                    data-testid="chatbot-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    className="chatbot-send"
                    aria-label="Send message"
                    data-testid="chatbot-send-btn"
                  >
                    {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
